// ============================================================
// EstimatesPanel.js
// The estimates on a quote or invoice, and the controls to modify or remove
// each one. Shared by NewDocument and EditDocument so the two pages cannot
// drift apart — the same reason the import itself lives in utils/estimates.js.
//
// Presentational only. It never navigates, saves a draft or edits document
// state; each page passes its own onModify(estimate) and onRemove(estimate).
// Removal IS confirmed here, so the wording of that confirm exists once.
//
// Two pieces, because the one-estimate case belongs where the old single
// Modify button was and the list does not fit there:
//
//   EstimateModifyControl   exactly ONE estimate: "Modify <label>" in the
//                           Line Items header row, as the single button was,
//                           plus a small ✕ to remove it.
//   EstimatesList           TWO OR MORE: one compact row per estimate, below
//                           the header row — label, line count, Modify, ✕.
//                           Also carries the note about unattached lines,
//                           on its own when there are fewer than two estimates.
// ============================================================

import React from 'react';

const KIND_ICON = { chainlink: '🔧', wood: '🪵', vinyl: '🏳️', aluminum: '🔩' };
const iconFor = (kind) => KIND_ICON[kind] || '🔧';
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Lines per estimate, and estimator lines that belong to none of them — no
// estimateId, or an id matching no estimate on this document.
export function estimateLineCounts(estimates, lineItems) {
  const list = Array.isArray(estimates) ? estimates : [];
  const ids = new Set(list.map(e => e.id));
  const byId = {};
  list.forEach(e => { byId[e.id] = 0; });
  let unattached = 0;
  (Array.isArray(lineItems) ? lineItems : []).forEach(item => {
    if (!item || !item.fromEstimator) return;
    if (item.estimateId && ids.has(item.estimateId)) byId[item.estimateId] += 1;
    else unattached += 1;
  });
  return { byId, unattached };
}

// DESTRUCTIVE, and it sits a few pixels from a Modify control — so it always
// asks, naming the estimate and exactly how many lines go with it.
function confirmRemove(estimate, lineCount) {
  return window.confirm(
    `Remove this estimate?\n\n${estimate.label}\n\n`
    + `Its ${plural(lineCount, 'line item')} will be removed from the document with it. `
    + 'Every other estimate and all other lines stay exactly as they are.'
  );
}

const MODIFY_BUTTON = {
  background: '#E4F3FA', border: '1px solid #0096C7', color: '#0096C7',
  padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s',
  fontFamily: 'inherit',
};
const REMOVE_BUTTON = {
  background: '#FFFFFF', border: '1px solid #E9C3C3', color: '#A32D2D',
  borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', lineHeight: 1,
  fontFamily: 'inherit',
};
const lift = (e) => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; };
const drop = (e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; };

export function EstimateModifyControl({ estimates, lineItems, onModify, onRemove }) {
  if (!Array.isArray(estimates) || estimates.length !== 1) return null;
  const estimate = estimates[0];
  const count = estimateLineCounts(estimates, lineItems).byId[estimate.id] || 0;
  return (
    <div data-estimate-control style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
      <button
        type="button"
        data-estimate-modify={estimate.id}
        onClick={() => onModify(estimate)}
        title={`${estimate.label} — ${plural(count, 'line')}. Opens its estimator with its settings.`}
        style={{ ...MODIFY_BUTTON, maxWidth: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        onMouseEnter={lift}
        onMouseLeave={drop}
      >
        {iconFor(estimate.kind)} Modify {estimate.label}
      </button>
      <button
        type="button"
        data-estimate-remove={estimate.id}
        aria-label={`Remove estimate: ${estimate.label}`}
        title={`Remove this estimate and its ${plural(count, 'line')}`}
        onClick={() => { if (confirmRemove(estimate, count)) onRemove(estimate); }}
        style={{ ...REMOVE_BUTTON, padding: '0 11px' }}
      >
        ✕
      </button>
    </div>
  );
}

export function EstimatesList({ estimates, lineItems, onModify, onRemove }) {
  const list = Array.isArray(estimates) ? estimates : [];
  const { byId, unattached } = estimateLineCounts(list, lineItems);
  const showList = list.length >= 2;
  if (!showList && unattached === 0) return null;

  // Old documents can carry estimator lines that belong to no estimate. Said
  // plainly, never hidden, and deliberately NOT a control: there are no
  // settings to re-open them with, and nothing here can modify or remove them.
  const note = unattached > 0 ? (
    <p data-unattached-note style={{ margin: showList ? '8px 2px 0 2px' : '0 0 12px 2px', color: '#6B7C93', fontSize: 12 }}>
      ℹ️ {plural(unattached, 'estimator line')} from an earlier version — not editable
    </p>
  ) : null;

  if (!showList) return note;

  return (
    <div
      data-estimates-list
      style={{ marginBottom: 12, border: '1px solid #E3ECF5', borderRadius: 12, background: '#F8FBFE', padding: '8px 12px' }}
    >
      <p style={{ margin: '0 0 2px 2px', color: '#6B7C93', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
        Estimates ({list.length})
      </p>
      {list.map((estimate, i) => {
        const count = byId[estimate.id] || 0;
        return (
          <div
            key={estimate.id}
            data-estimate-row={estimate.id}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px', borderTop: i ? '1px solid #E3ECF5' : 'none' }}
          >
            <span aria-hidden="true" style={{ fontSize: 14, flexShrink: 0 }}>{iconFor(estimate.kind)}</span>
            <span
              data-estimate-label
              title={estimate.label}
              style={{ flex: 1, minWidth: 0, color: '#03045E', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {estimate.label}
            </span>
            {/* The count is what tells two similar-looking estimates apart. */}
            <span data-estimate-count style={{ color: '#6B7C93', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {plural(count, 'line')}
            </span>
            <button
              type="button"
              data-estimate-modify={estimate.id}
              onClick={() => onModify(estimate)}
              title={`Open the estimator for ${estimate.label}`}
              style={{ ...MODIFY_BUTTON, padding: '5px 12px', fontSize: 12, flexShrink: 0 }}
              onMouseEnter={lift}
              onMouseLeave={drop}
            >
              Modify
            </button>
            {/* Set a little apart from Modify, and it always confirms. */}
            <button
              type="button"
              data-estimate-remove={estimate.id}
              aria-label={`Remove estimate: ${estimate.label}`}
              title={`Remove this estimate and its ${plural(count, 'line')}`}
              onClick={() => { if (confirmRemove(estimate, count)) onRemove(estimate); }}
              style={{ ...REMOVE_BUTTON, padding: '5px 9px', fontSize: 12, marginLeft: 6, flexShrink: 0 }}
            >
              ✕
            </button>
          </div>
        );
      })}
      {note}
    </div>
  );
}
